import React, { useMemo, useState } from "react";

import { getIn, useFormikContext } from "formik";
import { useSelector } from "react-redux";

import { LabelSelectorField } from "components/Form/Fields";
import { LabelSelectFieldOption } from "components/Form/FormikControls";

import { ScanLabelArchetype } from "constants/scanLabels";

import { EMPTY_ARRAY } from "lib";
import { getLivestockSaleId } from "lib/navigation";

import { getScanLabels, selectScanLabelsByScanIdLookup } from "selectors";

import { getCheckboxFieldName } from "./util";

export interface ScanLabelOption extends LabelSelectFieldOption {
  archetype: ScanLabelArchetype;
}

function sortedOptions(options: ScanLabelOption[]): ScanLabelOption[] {
  return [...options].sort((a, b) => a.label.localeCompare(b.label));
}

interface ScanLabelSelectorProps {
  modelIds: string[];
  name: string;
}

export const ScanLabelSelector: React.FC<ScanLabelSelectorProps> = ({
  modelIds,
  name,
}) => {
  const [customLabelNames, setCustomLabelNames] = useState<string[]>([]);
  const { values, setFieldValue } = useFormikContext();

  const isRemove = name === "removeLabels";
  const isSelected = getIn(values, getCheckboxFieldName(name));

  const scanLabelsById = useSelector(getScanLabels);
  const scanLabelsByScanId = useSelector(selectScanLabelsByScanIdLookup);
  const livestockSaleId = getLivestockSaleId();

  const baseOptions = useMemo(() => {
    const optionsByName = new Map<string, ScanLabelOption>();

    Object.values(ScanLabelArchetype)
      .filter(archetype => archetype !== ScanLabelArchetype.NONE)
      .forEach(archetype =>
        optionsByName.set(archetype, {
          label: archetype,
          value: archetype,
          archetype,
        }),
      );

    Object.values(scanLabelsById as Record<string, ScanLabel>)
      .filter(scanLabel => scanLabel.livestockSaleId === livestockSaleId)
      .forEach(scanLabel => {
        if (!optionsByName.has(scanLabel.name)) {
          optionsByName.set(scanLabel.name, {
            label: scanLabel.name,
            value: scanLabel.name,
            archetype: scanLabel.archetype,
          });
        }
      });

    customLabelNames.forEach(customLabelName => {
      if (!optionsByName.has(customLabelName)) {
        optionsByName.set(customLabelName, {
          label: customLabelName,
          value: customLabelName,
          archetype: ScanLabelArchetype.NONE,
        });
      }
    });

    return sortedOptions(Array.from(optionsByName.values()));
  }, [scanLabelsById, livestockSaleId, customLabelNames]);

  const namesOnSelectedScans = useMemo(() => {
    const names = new Set<string>();
    modelIds.forEach(scanId =>
      (scanLabelsByScanId[scanId] || []).forEach(scanLabel =>
        names.add(scanLabel.name),
      ),
    );
    return names;
  }, [modelIds, scanLabelsByScanId]);

  const options = isRemove
    ? baseOptions.filter(option => namesOnSelectedScans.has(option.value))
    : baseOptions;

  function onCreateLabel(labelName: string) {
    setCustomLabelNames(previous =>
      previous.includes(labelName) ? previous : [...previous, labelName],
    );
    const currentValues = getIn(values, name) || EMPTY_ARRAY;
    if (!currentValues.includes(labelName)) {
      setFieldValue(name, [...currentValues, labelName]);
    }
  }

  return (
    <LabelSelectorField
      name={name}
      label={isRemove ? "Labels to remove" : "Labels to add"}
      options={options}
      disabled={!isSelected}
      addLabel={isRemove ? null : onCreateLabel}
      placeHolder={isRemove ? "Click to remove labels" : "Click to add labels"}
    />
  );
};

export function buildScanLabelPayload(
  id: string,
  updatedValues: Record<string, unknown>,
): Record<string, unknown> {
  return {
    addLabels: (updatedValues.addLabels as string[]) || EMPTY_ARRAY,
    removeLabels: (updatedValues.removeLabels as string[]) || EMPTY_ARRAY,
  };
}
